/* eslint-disable react/react-in-jsx-scope,react/jsx-filename-extension,react/prop-types,react/no-array-index-key */

import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import _ from 'lodash';

import ColumnList from './column-list';
import parameterizeString from '../../../helpers/parameterize';
import Linking from '../../PageBuilder/helpers/linking';
import { cmsLegacyThemeConnector } from '../../PageBuilder/redux/connector';
import { dataTrackAttribute, mapPathFragmentFromDomain } from '../../helpers/analytics';
import { connectLocation } from '../../location/LocationConnectors';
import wrappedInReduxProvider from '../../wrappedInReduxProvider';
import RichText from '../oc-rich-text';
import Text from '../oc-text';

const targetProp = url => {
  const { opensInBlank } = Linking.Parse(url);
  return opensInBlank ? '_blank' : undefined;
};

const StyledLink = cmsLegacyThemeConnector(styled.a`
  background: ${props => props.legacyTheme.colors.brand};
  border: 1px solid ${props => props.legacyTheme.colors.brand};

  &:hover,
  &:focus {
    color: ${props => props.legacyTheme.colors.brand};
  }
`);

function CtaItem({ item, extraClass = null }) {
  const linkClasses = ['button', 'oc-cta-button'];
  if (extraClass) linkClasses.push(extraClass);

  return (
    <div className="call-to-action">
      <StyledLink
        className={linkClasses.join(' ')}
        href={item.details?.url}
        data-track="nav-click"
        target={targetProp(item.details?.url)}
      >
        <Text content={item.details?.title} />
      </StyledLink>
    </div>
  );
}

function MobileCtaItem({ item }) {
  return (
    <div className="mobile-ctas">
      <CtaItem item={item} />
    </div>
  );
}

function LinkItem({ item }) {
  const useTitleOverride = item.details?.showTitleOverride;
  const titleOverride = item.details?.titleOverride;
  const title = item.details?.title;

  return (
    <a
      aria-label={title}
      className="nav-link"
      href={item.details?.url}
      data-track="nav-click"
      data-track-nav-select={parameterizeString(title)}
      target={targetProp(item.details?.url)}
    >
      <RichText content={useTitleOverride ? titleOverride : title} />
    </a>
  );
}

function LinkDropdownItem({ item, locale }) {
  const useTitleOverride = item.details?.showTitleOverride;
  const titleOverride = item.details?.titleOverride;
  const title = item.details?.title;

  return (
    <div className="navbar-dropdown" data-track-top-level={parameterizeString(title)}>
      <div className="nav-dropdown-trigger" role="button" tabIndex="0" aria-expanded="false" aria-label={title} />

      {item.details?.url ? (
        <a
          className="nav-link-dropdown"
          href={item.details?.url}
          aria-label={title}
          data-track="nav-click"
          data-track-nav-select={dataTrackAttribute(item.details?.url, locale, true, mapPathFragmentFromDomain())}
          data-track-top-level={parameterizeString(title)}
          target={targetProp(item.details?.url)}
        >
          <div className="nav-dropdown__content" aria-hidden="true">
            <RichText content={useTitleOverride ? titleOverride : title} />
            <div className="hover-decal" />
          </div>
        </a>
      ) : (
        <div className="nav-dropdown__content" aria-hidden="true">
          <RichText content={useTitleOverride ? titleOverride : title} />
          <div className="hover-decal" />
        </div>
      )}

      <div className="dropdown-section" aria-hidden="true">
        <div className="nav-dropdown-content" role="menu">
          <ColumnList columns={item.children} />
        </div>
      </div>
    </div>
  );
}

function DropdownItem({ item }) {
  const useTitleOverride = item.details?.showTitleOverride;
  const titleOverride = item.details?.titleOverride;
  const title = item.details?.title;

  return (
    <div className="navbar-dropdown" data-track-top-level={parameterizeString(title)}>
      <div className="nav-dropdown-trigger" role="button" tabIndex="0" aria-expanded="false" aria-label={title} />

      <div className="nav-dropdown__content" aria-hidden="true">
        <RichText content={useTitleOverride ? titleOverride : title} />
        <div className="hover-decal" />
      </div>

      <div className="dropdown-section" role="group">
        <div className="nav-dropdown-content">
          <ColumnList columns={item.children} />
        </div>
      </div>
    </div>
  );
}

function PredictiveSearchResults() {
  return <div className="predictive-search-results" />;
}

function SearchBar({ siteName, searchLocale, searchPlaceholder, searchApiUrl, searchUrl }) {
  return (
    <div
      // eslint-disable-next-line no-undef
      className={`search-bar with-predictive-search tenant-${parameterizeString(siteName)}`}
      role="search"
      aria-label="Site Search"
    >
      <form
        action={searchUrl}
        data-track="search-bar"
        data-track-search-term=""
        target={searchLocale ? '_blank' : '_self'}
      >
        <div>
          <label className="sr-only" htmlFor="search">
            {searchPlaceholder}
          </label>
          <input
            type="search"
            name="q"
            id="search"
            placeholder={searchPlaceholder}
            data-search-api={searchApiUrl}
            data-search-locale={searchLocale}
          />
          {searchLocale && <input type="hidden" name="fields.label" value={searchLocale} />}
          <button className="search-button" type="submit" aria-label="Submit Search">
            <span aria-hidden="true" className="fa fa-search fa-md" />
          </button>
        </div>
      </form>
      <PredictiveSearchResults />
    </div>
  );
}

function MobileSearchBar({ searchLocale, searchPlaceholder, searchApiUrl, searchUrl }) {
  return (
    <form
      className="search-bar__mobile"
      action={searchUrl}
      data-track="search-bar"
      data-track-search-term=""
      target={searchLocale ? '_blank' : '_self'}
    >
      <label className="sr-only" htmlFor="q">
        {searchPlaceholder}
      </label>
      <input
        type="search"
        name="q"
        placeholder={searchPlaceholder}
        data-search-api={searchApiUrl}
        data-search-locale={searchLocale}
      />
      {searchLocale && <input type="hidden" name="fields.label" value={searchLocale} />}
      <button className="search-button" type="submit" aria-label="search submit">
        <span aria-hidden="true" className="fa fa-search fa-md" />
      </button>
    </form>
  );
}

const MegaNav = connectLocation(
  ({
    t,
    siteName,
    locale: { code },
    meganavItems,
    navFooterItems,
    searchLocale,
    searchPlaceholder,
    searchApiUrl,
    searchUrl,
  }) => {
    const mid = Math.ceil((navFooterItems || []).length / 2);
    const leftNavFooterItems = (navFooterItems || []).slice(0, mid);
    const rightNavFooterItems = (navFooterItems || []).slice(mid);
    // eslint-disable-next-line no-undef
    const tenantClass = `tenant-${parameterizeString(siteName)}`;
    const meganavClasses = ['oc-meganav', tenantClass];
    const navLogoClasses = ['navbar-brand', tenantClass];
    const mobileNavButtonClasses = ['mobile-nav__toggle', tenantClass];
    return (
      <>
        <Global
          styles={css`
            .image-parent {
              padding-bottom: 24px;
            }
            .image-parent:last-of-type {
              padding-bottom: 0;
            }
          `}
        />
        <div className={meganavClasses.join(' ')} role="navigation" aria-label="Main Navigation">
          <div className="nav-wrapper">
            <div className={`desktop-nav ${tenantClass}`}>
              <div className="navbar navbar-default">
                <div className={`nav-content ${tenantClass}`} data-track-nav-loc="header">
                  <div className="nav-content-container">
                    <div className="navbar-header">
                      <a
                        className={navLogoClasses.join(' ')}
                        href="/"
                        data-track="nav-click"
                        data-track-top-level="home"
                      >
                        {/* eslint-disable-next-line no-undef */}
                        {siteName}
                      </a>
                    </div>
                    <nav className="dropdowns" role="navigation" aria-label="Main Menu">
                      {meganavItems.map((item, i) => {
                        if (item.details?.cta) {
                          return <CtaItem key={i} item={item} extraClass={tenantClass} />;
                        }
                        if (!item.details?.url && item.children?.length >= 1) {
                          return <DropdownItem key={i} item={item} />;
                        }
                        if (item.children?.length >= 1) {
                          return <LinkDropdownItem key={i} item={item} locale={code} />;
                        }
                        return <LinkItem key={i} item={item} />;
                      })}
                    </nav>
                  </div>
                  {code !== 'en-CA' && code !== 'fr-CA' && (
                    <SearchBar
                      t={t}
                      locale={code}
                      siteName={siteName}
                      searchLocale={searchLocale}
                      searchPlaceholder={searchPlaceholder}
                      searchApiUrl={searchApiUrl}
                      searchUrl={searchUrl}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="mobile-nav" data-track-nav-loc="mobile header">
              <nav className="navbar navbar-default">
                <div className="nav-content" data-track="nav-click">
                  <div className="navbar-header">
                    <a className={navLogoClasses.join(' ')} href="/">
                      {/* eslint-disable-next-line no-undef */}
                      {siteName}
                    </a>
                  </div>

                  <div className="navbar__right">
                    <div className="right__positioner">
                      {meganavItems
                        .filter(item => item.details?.cta)
                        .map((item, i) => (
                          <MobileCtaItem key={i} item={item} />
                        ))}
                      {/* eslint-disable-next-line react/button-has-type */}
                      <button
                        data-track="menu-open"
                        className={mobileNavButtonClasses.join(' ')}
                        aria-label="hamburger button"
                      >
                        <span className="sr-only">Hamburger Menu</span>
                        <span id="hamburger__icon" aria-hidden="true" className="fa fa-bars fa-lg" />
                      </button>
                    </div>
                  </div>
                </div>
              </nav>
              <div
                id="mobile-nav-panel"
                className="mobile-nav__panel faded-nav fade-in-exit-done"
                role="search"
                aria-label="Site Search"
              >
                <div className="nav-border slide-panel secondary-panel-1 main-panel-enter-done with-predictive-search">
                  {code !== 'en-CA' && code !== 'fr-CA' && (
                    <MobileSearchBar
                      t={t}
                      locale={code}
                      searchLocale={searchLocale}
                      searchPlaceholder={searchPlaceholder}
                      searchApiUrl={searchApiUrl}
                      searchUrl={searchUrl}
                    />
                  )}
                  <div className="mobile-nav__list">
                    {meganavItems
                      .filter(item => !item.details?.cta)
                      .map((item, index) => {
                        if (item.details?.url && _.isEmpty(item.children)) {
                          return (
                            <div key={index} className="mobile-nav__list-item">
                              <a href={item.details.url} data-track="nav-click" target={targetProp(item.details.url)}>
                                {item.details.title}
                              </a>
                            </div>
                          );
                        }
                        return (
                          <div
                            className="mobile-nav__list-item"
                            data-structure="mobile-nav-list-item"
                            data-target={item.details?.title}
                            data-track="nav-reveal"
                            data-track-top-level={parameterizeString(item.details?.title)}
                            key={index}
                          >
                            <div className="list-item__content">{item.details?.title}</div>
                            <div className="list-item__chevron">
                              <span aria-hidden="true" className="fa fa-chevron-right fa-md" />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {navFooterItems?.length > 0 && (
                    <div className="mobile-nav__bottom-links">
                      {leftNavFooterItems && (
                        <div className="bottom-links__left-column">
                          {leftNavFooterItems.map((item, i) => (
                            <a key={i} href={item.url} target={targetProp(item.url)}>
                              {item.title}
                            </a>
                          ))}
                        </div>
                      )}
                      {rightNavFooterItems && (
                        <div className="bottom-links__right-column">
                          {rightNavFooterItems.map((item, i) => (
                            <a key={i} href={item.url} target={targetProp(item.url)}>
                              {item.title}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <PredictiveSearchResults />
                </div>
                <div className="nav-border slide-panel secondary-panel-2 secondary-panel-exit-done">
                  {meganavItems
                    .filter(item => item.children)
                    .map((item, i) => (
                      <div key={i} id={item.details?.title} className="mobile-nav__active-selection">
                        {code !== 'en-CA' && code !== 'fr-CA' && (
                          <MobileSearchBar
                            t={t}
                            locale={code}
                            searchLocale={searchLocale}
                            searchPlaceholder={searchPlaceholder}
                            searchApiUrl={searchApiUrl}
                            searchUrl={searchUrl}
                          />
                        )}
                        <div className="active-selection__back-button">
                          <span aria-hidden="true" className="fa fa-caret-left fa-md" />
                          <span>Back</span>
                        </div>

                        <div className="active-selection__header">{item.details?.title}</div>

                        <div
                          className="active-selection__content"
                          data-track-top-level={parameterizeString(item.details?.title)}
                        >
                          <ColumnList columns={item.children} mobile />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
);
/* eslint-enable react/react-in-jsx-scope */

export default MegaNav;

export const ProvidedMegaNav = wrappedInReduxProvider(MegaNav);
